import React, { ReactNode, useContext, useEffect, useMemo } from 'react'
import { withLDProvider, useFlags, useLDClient, LDContext } from 'launchdarkly-react-client-sdk'
import Observability, { LDObserve } from '@launchdarkly/observability'
import { AuthContext } from '../AuthContextProvider'

type Props = {
  children: ReactNode
}

const LAUNCHDARKLY_CLIENT_ID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || ''

const observabilityPlugin = new Observability({
  serviceName: 'keee-web',
  // Distributed tracing is disabled because it injects a `traceparent` header
  // into outgoing requests, which the API rejects in CORS preflight
  // (Access-Control-Allow-Headers does not list it), breaking every API call.
  // To enable end-to-end tracing, the backend (keee-api) must allowlist
  // `traceparent`, `tracestate`, and `x-highlight-request` first.
  tracingOrigins: false,
  networkRecording: {
    enabled: true,
    // Record that requests happened (timing, status) but not headers or
    // bodies, which carry auth tokens and PII.
    recordHeadersAndBody: false,
  },
})

/**
 * Component that syncs the authenticated user to LaunchDarkly.
 * This must be rendered inside both AuthContext and LDProvider.
 */
function LaunchDarklyUserSync({ children }: Props) {
  const { currentAuthenticatedUser, currentRole } = useContext(AuthContext)
  const ldClient = useLDClient()

  const userContext: LDContext = useMemo(() => {
    if (!currentAuthenticatedUser) {
      return {
        kind: 'user',
        anonymous: true,
        key: 'anonymous-user',
      }
    }

    const tenantId = currentAuthenticatedUser.attributes?.['custom:tenant_id']
    const email = currentAuthenticatedUser.attributes?.email
    const userId = currentAuthenticatedUser.username

    return {
      kind: 'multi',
      user: {
        key: userId,
        email: email,
        tenantId: tenantId || '',
        roleId: currentRole?.role_id || '',
        roleName: currentRole?.name || '',
      },
      tenant: {
        key: tenantId || 'unknown',
      },
    }
  }, [currentAuthenticatedUser, currentRole])

  useEffect(() => {
    if (ldClient) {
      ldClient.identify(userContext)
    }
  }, [ldClient, userContext])

  return <>{children}</>
}

/**
 * Internal component that wraps children with LaunchDarkly context.
 * The actual provider is created by withLDProvider HOC.
 */
function LaunchDarklyInner({ children }: Props) {
  return <LaunchDarklyUserSync>{children}</LaunchDarklyUserSync>
}

/**
 * HOC-wrapped provider that initializes LaunchDarkly with user context.
 * Wrap this around your app inside AuthContextProvider.
 */
export const LaunchDarklyProvider = LAUNCHDARKLY_CLIENT_ID
  ? withLDProvider({
      clientSideID: LAUNCHDARKLY_CLIENT_ID,
      reactOptions: {
        useCamelCaseFlagKeys: true,
      },
      options: {
        // Bootstrap from localStorage to avoid flash of default content
        bootstrap: 'localStorage',
        // Send analytics events
        sendEvents: true,
        // Capture errors, console logs, traces, and network timing
        plugins: [observabilityPlugin],
      },
      context: {
        kind: 'user',
        anonymous: true,
        key: 'anonymous-user',
      },
    })(LaunchDarklyInner)
  : ({ children }: Props) => <>{children}</>

/**
 * Hook to get all feature flags.
 * Returns an object with all flags where keys are camelCased flag names.
 *
 * @example
 * const flags = useFeatureFlags()
 * if (flags.newDashboard) {
 *   // render new dashboard
 * }
 */
export { useFlags as useFeatureFlags }

/**
 * Hook to get the LaunchDarkly client for advanced operations.
 * Use this for:
 * - Tracking custom events
 * - Getting variation details
 *
 * @example
 * const ldClient = useLDClient()
 * ldClient?.track('button-clicked', { buttonName: 'submit' })
 */
export { useLDClient }

/**
 * Custom hook for a single feature flag with a default value.
 *
 * @param flagKey - The camelCased flag key
 * @param defaultValue - Default value if flag is not found
 * @returns The flag value
 *
 * @example
 * const showNewFeature = useFeatureFlag('showNewFeature', false)
 */
export function useFeatureFlag<T>(flagKey: string, defaultValue: T): T {
  const flags = useFlags()
  return (flags[flagKey] as T) ?? defaultValue
}

/**
 * Structured logging that forwards to LaunchDarkly observability.
 *
 * Safe to call before LaunchDarkly initializes (calls are buffered) and when
 * LaunchDarkly is disabled (no client ID), in which case they are no-ops.
 *
 * @example
 * logger.info('Badge generated', { tenantId })
 * logger.error(err, 'Failed to save person')
 */
export const logger = {
  info: (message: string, metadata?: Record<string, string>) => LDObserve.recordLog(message, 'info', metadata),
  warn: (message: string, metadata?: Record<string, string>) => LDObserve.recordLog(message, 'warn', metadata),
  error: (error: Error, message?: string, payload?: Record<string, string>) =>
    LDObserve.recordError(error, message, payload),
}

/**
 * Direct access to the LaunchDarkly observability client for advanced use
 * (custom metrics, spans). Prefer `logger` for plain logging.
 */
export { LDObserve }
